<template>
  <div class="user-detail">
    <APageHead title="관리자 상세" :back="true" @back="$router.back()" />

    <div class="user-detail__layout">
      <aside class="user-detail__side">
        <SideNav :sections="managerSideNav" />
      </aside>
      <main class="user-detail__main">
        <div v-if="loading" class="user-detail__loading">
          <div class="p-spinner p-spinner--lg"></div>
          <p>관리자 정보를 불러오는 중...</p>
        </div>

        <template v-else-if="managerData">
          <BasicInfoCard
            id="basic-info"
            :basic-data="managerData"
            user-type="admin"
            @update-field="handleUpdateField"
            @save-changes="handleSaveChanges"
          />
          <SystemAccessLogCard id="system-access-log" :admin-idx="managerId" />
          <AccountStatusCard
            id="account-status"
            :current-status="managerData.active_yn"
            :is-employee="true"
            :allow-delete="true"
            @deactivate-account="handleDeactivateAccount"
            @activate-account="handleActivateAccount"
            @delete-account="handleDeleteAccount"
          />
        </template>

        <AEmpty v-else title="관리자 정보를 찾을 수 없어요" />
      </main>
    </div>
  </div>
</template>

<script>
import BasicInfoCard from "@/views/users/components/BasicInfoCard.vue";
import SystemAccessLogCard from "@/views/users/components/SystemAccessLogCard.vue";
import AccountStatusCard from "@/views/users/components/AccountStatusCard.vue";
import SideNav from "@/views/users/components/SideNav.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "ManagerDetail",
  components: { BasicInfoCard, SystemAccessLogCard, AccountStatusCard, SideNav, APageHead, AEmpty },
  data() {
    return {
      managerId: null,
      managerData: null,
      loading: false,
      updateFields: {},
      managerSideNav: [
        { id: "basic-info", icon: "receipt_long", label: "기본 정보" },
        { id: "system-access-log", icon: "history", label: "접속 로그" },
        { id: "account-status", icon: "verified_user", label: "계정 상태" },
      ],
    };
  },
  created() {
    this.managerId = this.$route.query.idx;
    if (this.managerId) this.loadManagerData();
  },
  methods: {
    async loadManagerData() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/roles/employees/${this.managerId}`,
          loadingBar: true,
        });
        if (res.status === 200) this.managerData = res.data;
      } catch (error) {
        console.error("관리자 정보 로드 실패:", error);
        alert(error.response?.status === 404 ? "관리자를 찾을 수 없습니다." : "관리자 정보를 불러오는데 실패했습니다.");
      } finally {
        this.loading = false;
      }
    },
    handleUpdateField({ field, value }) {
      this.updateFields = { ...this.updateFields, [field]: value };
    },
    async handleSaveChanges() {
      if (Object.keys(this.updateFields).length === 0) {
        alert("변경된 내용이 없습니다.");
        return;
      }
      try {
        await this.$axios({
          method: "put",
          url: `/roles/employees/${this.managerId}`,
          data: this.updateFields,
        });
        alert("관리자 정보가 성공적으로 수정되었습니다.");
        this.updateFields = {};
        await this.loadManagerData();
      } catch (error) {
        const msg = error.response?.data?.detail || error.response?.data?.message || "관리자 정보 수정에 실패했습니다.";
        alert(msg);
      }
    },
    async patchStatus(status, message) {
      try {
        await this.$axios({
          method: "patch",
          url: `/roles/employees/${this.managerId}/status`,
          data: { status },
          loadingBar: true,
        });
        alert(message);
        await this.loadManagerData();
      } catch (error) {
        alert(error.response?.data?.detail || "처리에 실패했습니다.");
      }
    },
    handleDeactivateAccount() {
      if (window.confirm("정말 이 계정을 비활성화하시겠습니까?")) this.patchStatus("N", "계정이 비활성화되었습니다.");
    },
    handleActivateAccount() {
      if (window.confirm("정말 이 계정을 활성화하시겠습니까?")) this.patchStatus("Y", "계정이 활성화되었습니다.");
    },
    async handleDeleteAccount() {
      if (!window.confirm("정말 이 관리자 계정을 삭제하시겠습니까?\n계정 데이터가 완전히 삭제되며 복구할 수 없습니다.")) return;
      try {
        const res = await this.$axios({
          method: "delete",
          url: `/roles/employees/${this.managerId}`,
          loadingBar: true,
        });
        alert(res.data?.message || "계정이 삭제되었습니다.");
        this.$router.push({ name: "UsersManagers" });
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        let msg;
        if (status === 403) msg = detail || "이 계정은 삭제할 수 없습니다.";
        else if (status === 404) msg = "이미 삭제되었거나 존재하지 않는 계정입니다.";
        else msg = detail || "계정 삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.";
        alert(msg);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.user-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__layout {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 16px;
    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__side { min-width: 0; }
  &__main { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
  &__loading {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 64px 24px; color: $p-ink-mid;
    background: $p-panel; border: 1px solid $p-line; border-radius: $p-radius-lg;
    p { margin: 0; font-size: 12px; }
  }
}
</style>
